// EnrolmentCourseGrid.tsx
/**
 * The landing view for `/Grow/Enrolment`: the facilitator's enrolments rolled up **by course**, so
 * someone working on one course at a time can go straight to it instead of scrolling a flat list of
 * everything they run.
 *
 * Clicking a course drills into `EnrolmentsListView` filtered to that course **and to Active**.
 * Pending invitations are deliberately excluded: they belong on Pending Enrolments, where they can be
 * accepted or declined. Active Enrolments stays the flat list of the same set, ungrouped.
 *
 * Grouped by **`courseID`** (SDK 0.0.50). It used to group by `courseName`, which collapsed two
 * distinct courses sharing a name into a single card — and the drill-down then matched on that name,
 * so it showed both courses' enrolments together. ⚠️ Do not go back to name-keying.
 * See `_deploy/_newEndPoints/` if that becomes real.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Card, Container, Group, SimpleGrid, Skeleton, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import { IconBooks, IconChevronRight, IconRefresh, IconSearch } from '@tabler/icons-react';
import { TextInput } from '@/components';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';
import { useFacilitatorPortalService, FacilitatorRegistrationView } from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { REGISTRATION_STATUS } from '@/pages/Grow/registrationStatusIds';
import FacilitatorPortalNotice from './FacilitatorPortalNotice';

interface CourseGroup {
  courseID: number;
  courseName: string;
  courseColor?: string | null;
  total: number;
  /** Soonest upcoming session across this course's enrolments, when any is scheduled. */
  nextSessionDisplay?: string | null;
  leadCount: number;
}

interface EnrolmentCourseGridProps {
  onSelectCourse: (courseID: number) => void;
}

export default function EnrolmentCourseGrid({ onSelectCourse }: EnrolmentCourseGridProps) {
  const portalService = useFacilitatorPortalService();
  const { refetch } = useSidebarCountsContext();

  const [registrations, setRegistrations] = useState<FacilitatorRegistrationView[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await portalService.myRegistrations();
      setRegistrations(res ?? []);
      refetch();
    } catch (e) {
      console.error('Error fetching enrolments:', e);
      setLoadError(e);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  }, [portalService, refetch]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /**
   * Active only. This page is "the courses I am currently running", so pending invitations do not
   * belong: they live on Pending Enrolments, where they can be accepted or declined, and counting
   * them here would overstate the workload.
   */
  const activeRegistrations = useMemo(
    () => registrations.filter((r) => r.statusID === REGISTRATION_STATUS.Active),
    [registrations],
  );

  const courses = useMemo<CourseGroup[]>(() => {
    const byCourse = new Map<number, CourseGroup>();

    activeRegistrations.forEach((r) => {
      const name = r.courseName?.trim() || 'Unnamed course';
      const existing = byCourse.get(r.courseID) ?? {
        courseID: r.courseID,
        courseName: name,
        courseColor: r.courseColor,
        total: 0,
        nextSessionDisplay: null,
        leadCount: 0,
      };

      existing.total += 1;
      if (r.isLead) existing.leadCount += 1;
      // First non-empty wins. The portal view gives a preformatted string, not a sortable date,
      // so picking the *soonest* is not possible here without the raw timestamp.
      if (!existing.nextSessionDisplay && r.nextSessionDisplay) {
        existing.nextSessionDisplay = r.nextSessionDisplay;
      }

      byCourse.set(r.courseID, existing);
    });

    return [...byCourse.values()].sort((a, b) => b.total - a.total);
  }, [activeRegistrations]);

  const visibleCourses = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return courses;
    return courses.filter((c) => c.courseName.toLowerCase().includes(term));
  }, [courses, searchTerm]);

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" align="flex-end" wrap="nowrap" mb="lg">
        <div>
          <Title order={2}>Enrolment</Title>
          <Text c="dimmed" size="sm">
            The courses you are actively facilitating. Pick one to see just its enrolments.
          </Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" size="lg" onClick={fetchAll} loading={loading}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {loadError ? (
        <FacilitatorPortalNotice error={loadError} subject="your enrolments" onRetry={fetchAll} />
      ) : (
        <>
          <Card withBorder radius="md" padding="lg" mb="md">
            <TextInput
              placeholder="Search courses…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '300px' } }}
              size="sm"
            />
          </Card>

          {loading && registrations.length === 0 ? (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} withBorder radius="md" padding="lg">
                  <Skeleton height={18} width="70%" mb="sm" />
                  <Skeleton height={34} width="30%" mb="sm" />
                  <Skeleton height={12} width="60%" />
                </Card>
              ))}
            </SimpleGrid>
          ) : visibleCourses.length === 0 ? (
            <Card withBorder radius="md" padding="lg">
              <EmptyState variant="no-data" entityLabel="active enrolments" />
            </Card>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {visibleCourses.map((c) => (
                <Card
                  key={c.courseID}
                  withBorder
                  radius="md"
                  padding="lg"
                  onClick={() => onSelectCourse(c.courseID)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectCourse(c.courseID);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Stack gap="sm" style={{ height: '100%' }}>
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(c.courseColor)}>
                        {c.courseName}
                      </Badge>
                      <IconChevronRight size={18} opacity={0.5} />
                    </Group>

                    <Group gap="xs" align="baseline">
                      <Text fw={700} size="32px" lh={1}>{c.total}</Text>
                      <Text size="sm" c="dimmed">
                        active enrolment{c.total === 1 ? '' : 's'}
                      </Text>
                    </Group>

                    <Group gap={6}>
                      {c.leadCount > 0 && (
                        <Badge size="sm" variant="light" color="blue">
                          lead on {c.leadCount}
                        </Badge>
                      )}
                    </Group>

                    <Group gap={6} wrap="nowrap" mt="auto">
                      <IconBooks size={14} opacity={0.6} />
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {c.nextSessionDisplay
                          ? `Next session: ${c.nextSessionDisplay}`
                          : 'No session scheduled'}
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </>
      )}
    </Container>
  );
}
